import { z } from "zod";

/**
 * REVİZYON ÖNERİSİ (CLOS-138 `[093]`).
 *
 * ### ⚠ Öneri ana metni DEĞİŞTİRMİYOR
 *
 * Kart: *"öneri ana metni, kaynak versiyonu veya versiyon rolünü OTOMATİK DEĞİŞTİRMEZ"* ve
 * *"öneri kabul edilse bile kaynak versiyon üzerine yazılmaz."* Bu model bir teklif kaydı;
 * uygulama kararı kendi kartında.
 */

export const CONTRACT_REVISION_STATUSES = [
  "open",
  "accepted",
  "rejected",
  "countered",
  "withdrawn",
  /** Hedef içerik değişmiş; öneri artık eski bir baza dayanıyor. */
  "stale",
  "resolved",
] as const;
export type ContractRevisionStatus = (typeof CONTRACT_REVISION_STATUSES)[number];

export const CONTRACT_REVISION_STATUS_NAMES: Record<ContractRevisionStatus, string> = {
  open: "Açık",
  accepted: "Kabul edildi",
  rejected: "Reddedildi",
  countered: "Karşı öneri verildi",
  withdrawn: "Geri çekildi",
  stale: "Eski versiyona dayanıyor",
  /**
   * ⚠ "Çözüldü" FORMAL KABUL DEĞİL (kartın doğrudan kuralı): tartışma kapandı demek, metin
   * kabul edildi demek değil. Onay ve mutabakat kendi domainlerinin işi.
   */
  resolved: "Çözüldü",
};

export const createContractRevisionSchema = z.object({
  sourceVersionId: z.uuid("Geçerli bir kaynak versiyon seçilmelidir"),
  targetClauseId: z.uuid("Geçerli bir madde seçilmelidir"),
  /** Blok verilmezse öneri MADDE BAŞLIĞINI hedefliyor. */
  targetBlockIndex: z.coerce.number().int().min(0).nullish(),
  /**
   * Hedefin kullanıcının GÖRDÜĞÜ hâli.
   *
   * ⚠ Zorunlu: sunucu bunu kaynak versiyondaki gerçek metinle karşılaştırıyor. Göndermeseydi
   * kullanıcı, ekranında görmediği bir metne öneri yapmış olabilir ve kimse fark etmezdi.
   */
  currentText: z.string().max(20_000, "Hedef metin en fazla 20.000 karakter olabilir"),
  proposedText: z
    .string({ error: "Önerilen metin zorunludur" })
    .min(1, "Önerilen metin zorunludur")
    .max(20_000, "Önerilen metin en fazla 20.000 karakter olabilir"),
  note: z.string().max(2000, "Not en fazla 2000 karakter olabilir").nullish(),
  /** Öneriyi kimin adına yapıyorsunuz — kendi çalışma alanınız ya da bir taraf. */
  representedPartyId: z.uuid().nullish(),
});
export type CreateContractRevisionInput = z.infer<typeof createContractRevisionSchema>;

export type ContractRevisionView = {
  id: string;
  sourceVersionId: string;
  sourceVersionNumber: number;
  targetClauseId: string;
  targetBlockIndex: number | null;
  currentText: string;
  proposedText: string;
  note: string | null;
  status: ContractRevisionStatus;
  visibility: "internal";
  createdByName: string;
  representedPartyName: string | null;
  createdAt: string;
  /**
   * Hedef içerik öneriden sonra DEĞİŞTİ mi.
   *
   * ⚠ Okuma anında hesaplanıyor, satıra yazılmıyor: yazsaydık, hedef değiştiğinde bayrağı
   * güncelleyecek bir arka plan işine ihtiyaç duyardık ve o iş gecikince ekran yanlış
   * söylerdi.
   */
  staleTarget: boolean;

  /** Kararı veren kişinin O ANDAKİ adı; hesap kapansa da geçmiş korunuyor. */
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  /** Kabul edilen öneri uygulandıysa doğan versiyon; uygulanmadıysa `null`. */
  appliedVersionId: string | null;
  appliedVersionNumber: number | null;
};

/**
 * KARAR: kabul ya da ret (CLOS-139 `[094]`).
 *
 * ⚠ Küme İKİ DEĞERLİ. `countered`, `withdrawn` ve `resolved` durumları modelde var ama
 * onları üreten akışlar (karşı öneri, geri çekme) EP09'un işi — kartın kapsam sınırı
 * *"temel kabul/ret command'ı"* diyor. Bugün üretemediğimiz bir kararı bu uçtan kabul
 * etmek, hiçbir kodun ele almadığı bir durumu ürüne sokmak olurdu (ADR-0014).
 */
export const CONTRACT_REVISION_DECISIONS = ["accepted", "rejected"] as const;
export type ContractRevisionDecision = (typeof CONTRACT_REVISION_DECISIONS)[number];

export const decideContractRevisionSchema = z.object({
  decision: z.enum(CONTRACT_REVISION_DECISIONS, { error: "Kabul ya da ret seçilmelidir" }),
  /**
   * Kararın gerekçesi — isteğe bağlı.
   *
   * ⚠ Zorunlu kılmadık: ürün kuralı zorunlu tutmuyor ve zorunlu bir alan, karar veren
   * kişiyi anlamsız bir cümle yazmaya iter. Metin audit'e KOPYALANMIYOR (uzunluğu yazılıyor).
   */
  note: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
  /**
   * BAYAT öneriyi bilerek kabul ediyorum.
   *
   * ⚠ Kart: *"öneri eski kalmışsa kullanıcı UYARILIR; sessiz uygulama yapılmaz."* Uyarıyı
   * ölçülebilir kılan tek şey açık bir onay: onaysız kabul 409 alıyor. Ret için
   * istenmiyor — reddetmek güvenli bir işlem.
   */
  acknowledgeStale: z.boolean().optional(),
});
export type DecideContractRevisionInput = z.infer<typeof decideContractRevisionSchema>;

/**
 * Karar ve uygulama uçlarının yol parametreleri.
 *
 * ⚠ `idParamSchema` YETMEZ: bilinmeyen alanı reddeden şema, iki parametreli bir yolda
 * `revisionId`'yi fazlalık sayıp 422 üretir (`[091]`'de yaşandı).
 */
export const contractRevisionParamsSchema = z.object({
  id: z.uuid("Geçerli bir sözleşme seçilmelidir"),
  revisionId: z.uuid("Geçerli bir revizyon seçilmelidir"),
});
